import React, { useEffect, useMemo, useState } from 'react';
import type { UnitSelector, UnitSelectorWithAdditional } from 'app/types/costAndFees.type';
import {
  Divider,
  DropdownContainer,
  DropdownTrigger,
  GroupCount,
  GroupHeader,
  GroupTitle,
  UnitInfo,
  UnitItem,
  UnitItemTopRow,
  UnitName,
  UnitPrice,
  UnitSelectorWrapper,
} from './styles';
import { useCostsAndFeesContext } from '../../context/CostsAndFeesProvider';

const getBedDisplayName = (count: number): string => {
  if (count === 0) {
    return 'Studio';
  }
  return `${count} bd`;
};

interface UnitSelectorProps {
  selectedUnit: UnitSelector | null;
  onUnitSelect: (unit: UnitSelector | null) => void;
  selectedBedCounts: Array<number>;
  allBedsSelected: boolean;
}

export default function UnitSelector({
  selectedUnit,
  onUnitSelect,
  selectedBedCounts,
  allBedsSelected,
}: UnitSelectorProps) {
  const { calculatedCostsAndFees, getDisplayAmount, unitOptionsGrouped } = useCostsAndFeesContext();
  const [isOpen, setIsOpen] = useState(false);

  const allUnits = useMemo(() => {
    if (unitOptionsGrouped && Object.keys(unitOptionsGrouped).length > 0) {
      return Object.values(unitOptionsGrouped).flat();
    }
    return calculatedCostsAndFees?.selectors?.units || [];
  }, [unitOptionsGrouped, calculatedCostsAndFees?.selectors?.units]);

  const filteredUnits = useMemo(() => {
    if (allBedsSelected) {
      return allUnits;
    }
    if (selectedBedCounts.length === 0 && !allBedsSelected) {
      return [];
    }
    return allUnits.filter((unit) => selectedBedCounts.includes(unit.numBeds));
  }, [allUnits, selectedBedCounts, allBedsSelected]);

  const groupedUnitsForDisplay = useMemo(() => {
    const groups: Record<string, Array<UnitSelectorWithAdditional>> = {};
    filteredUnits.forEach((unit) => {
      const bedCountKey = String(unit.numBeds);
      if (!groups[bedCountKey]) {
        groups[bedCountKey] = [];
      }
      groups[bedCountKey].push(unit as UnitSelectorWithAdditional);
    });
    return Object.entries(groups).sort(([a], [b]) => Number(a) - Number(b));
  }, [filteredUnits]);

  useEffect(() => {
    setIsOpen(false);
  }, [selectedBedCounts, allBedsSelected, selectedUnit]);

  const triggerText = useMemo(() => {
    if (selectedUnit) {
      return selectedUnit.selector.displayName || getBedDisplayName(selectedUnit.numBeds);
    }
    const count = filteredUnits.length;
    if (count === 0 && !allBedsSelected) return 'No matching units';
    if (count === 0 && allBedsSelected) return 'No units available';
    if (count === 1) return `1 unit available`;
    return `${count} units available`;
  }, [selectedUnit, filteredUnits, allBedsSelected]);

  if (!calculatedCostsAndFees || allUnits.length === 0) {
    return null;
  }

  const handleUnitClick = (unit: UnitSelector) => {
    onUnitSelect(unit);
    setIsOpen(false);
  };

  const toggleDropdown = () => setIsOpen(!isOpen);

  return (
    <UnitSelectorWrapper>
      <DropdownTrigger onClick={toggleDropdown} disabled={filteredUnits.length === 0 && !selectedUnit}>
        {triggerText}
        {isOpen ? ' ▲' : ' ▼'}
      </DropdownTrigger>

      {isOpen && (
        <DropdownContainer>
          {groupedUnitsForDisplay.length === 0 && (
            <UnitItem style={{ fontStyle: 'italic', cursor: 'default' }}>No units match selection</UnitItem>
          )}
          {groupedUnitsForDisplay.map(([bedCountStr, unitGroup], index) => {
            const bedCount = Number(bedCountStr);
            const bedName = bedCount === 0 ? 'Studio' : `${bedCount} bedroom${bedCount !== 1 ? 's' : ''}`;

            return (
              <React.Fragment key={bedCount}>
                {index > 0 && <Divider />}
                <GroupHeader>
                  <GroupTitle>{bedName}</GroupTitle>
                  <GroupCount>{unitGroup.length} available</GroupCount>
                </GroupHeader>
                {unitGroup.map((unit) => {
                  const unitDisplayName = unit.selector.displayName;
                  const primaryDisplayInName = unitDisplayName || getBedDisplayName(unit.numBeds);

                  const unitInfoParts = [];
                  if (unitDisplayName) {
                    unitInfoParts.push(getBedDisplayName(unit.numBeds));
                  }
                  if (unit.numBaths != null) {
                    unitInfoParts.push(`${unit.numBaths} ba`);
                  }
                  if (unit.availableFrom) {
                    unitInfoParts.push(`Avail. ${unit.availableFrom}`);
                  }
                  const isInfoEmpty = unitInfoParts.length === 0;

                  return (
                    <UnitItem
                      key={unit.unitZpid || unit.selector.displayName || `unit-${unit.numBeds}-${index}`}
                      onClick={() => handleUnitClick(unit)}
                      title={`Select unit ${primaryDisplayInName}`}
                      isEmptyInfo={isInfoEmpty}
                      data-testid="unit-item"
                    >
                      <UnitItemTopRow>
                        <UnitName>{primaryDisplayInName}</UnitName>
                        <UnitPrice>{getDisplayAmount?.(unit.selector.costs?.monthly?.amount) ?? 'N/A'}</UnitPrice>
                      </UnitItemTopRow>
                      <UnitInfo isEmpty={isInfoEmpty}>{isInfoEmpty ? '' : unitInfoParts.join(' | ')}</UnitInfo>
                    </UnitItem>
                  );
                })}
              </React.Fragment>
            );
          })}
        </DropdownContainer>
      )}
    </UnitSelectorWrapper>
  );
}
